import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { RefreshCw, ScrollText, Search } from 'lucide-react';
import { apiGet, formatTs, BOT_LABELS } from './ops_helpers';

const LEVELS = ['ALL', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];
const BOTS = ['ALL', 'hyperliquid', 'ftmo'];

const LEVEL_STYLE = {
  INFO: 'bg-sky-600/20 text-sky-300 border-sky-700/40',
  WARNING: 'bg-amber-600/20 text-amber-300 border-amber-700/40',
  ERROR: 'bg-rose-600/20 text-rose-300 border-rose-700/50',
  CRITICAL: 'bg-rose-700/30 text-rose-200 border-rose-600',
  DEBUG: 'bg-zinc-700/30 text-zinc-400 border-zinc-700',
};

const Pill = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${
      active
        ? 'bg-violet-600/30 text-violet-200 border-violet-600'
        : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200'
    }`}
  >
    {children}
  </button>
);

export function LogsPanel({ isAdmin = false }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bot, setBot] = useState('ALL');
  const [level, setLevel] = useState('ALL');
  const [q, setQ] = useState('');
  const [auto, setAuto] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '150' });
      if (bot !== 'ALL') params.set('bot_id', bot);
      if (level !== 'ALL') params.set('level', level);
      if (q.trim()) params.set('q', q.trim());
      const d = await apiGet(`/api/ops/logs?${params.toString()}`);
      setLogs(d.logs || []);
    } catch (e) {
      // silencioso: el panel muestra "sin logs"
    } finally {
      setLoading(false);
    }
  }, [bot, level, q]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  useEffect(() => {
    if (!isAdmin || !auto) return undefined;
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [isAdmin, auto, load]);

  if (!isAdmin) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="logs-no-admin">
        <CardContent className="py-8 text-center text-zinc-400">
          Acceso restringido. Solo administradores pueden ver los logs.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="logs-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-violet-400" /> Logs unificados
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              data-testid="logs-auto-toggle"
              variant="ghost" size="sm"
              className={auto ? 'text-emerald-300' : 'text-zinc-500'}
              onClick={() => setAuto((v) => !v)}
            >
              Auto {auto ? 'ON' : 'OFF'}
            </Button>
            <Button data-testid="logs-refresh-btn" variant="ghost" size="sm"
              className="text-zinc-400 hover:text-zinc-100" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {/* Filtros */}
        <div className="flex flex-col gap-2 pt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-zinc-500 mr-1">Bot:</span>
            {BOTS.map((b) => (
              <Pill key={b} active={bot === b} onClick={() => setBot(b)} testId={`logs-bot-${b}`}>
                {b === 'ALL' ? 'Todos' : (BOT_LABELS[b] || b)}
              </Pill>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-zinc-500 mr-1">Nivel:</span>
            {LEVELS.map((l) => (
              <Pill key={l} active={level === l} onClick={() => setLevel(l)} testId={`logs-level-${l}`}>
                {l === 'ALL' ? 'Todos' : l}
              </Pill>
            ))}
          </div>
          <div className="relative max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="logs-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder="Buscar en el mensaje... (Enter)"
              className="bg-zinc-950 border-zinc-800 text-zinc-200 pl-8 h-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm" data-testid="logs-empty">
            {loading ? 'Cargando…' : 'Sin logs para estos filtros.'}
          </div>
        ) : (
          <div className="space-y-1 font-mono text-xs max-h-[60vh] overflow-y-auto" data-testid="logs-list">
            {logs.map((l, i) => (
              <div key={i} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/40 border-b border-zinc-800/40">
                <span className="text-zinc-500 shrink-0 w-[130px]">{formatTs(l.ts)}</span>
                <Badge className={`shrink-0 ${LEVEL_STYLE[l.level] || LEVEL_STYLE.DEBUG}`}>{l.level}</Badge>
                <span className="text-zinc-400 shrink-0 w-[90px] truncate">{BOT_LABELS[l.bot_id] ? l.bot_id : l.bot_id}</span>
                {l.category && <span className="text-violet-300/70 shrink-0">[{l.category}]</span>}
                <span className="text-zinc-200 break-words">{l.message}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LogsPanel;
